import {
  faFileAlt,
  faPencilAlt,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import { B2Button } from "@hdb/biome-2";
import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import HasPermission from "../../../../../core/guards/has-permission";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../../shared/form-builder/form-builder.component";
import isObjectNullable from "../../../../../shared/helpers/isObjectNullable";
import {
  addTC,
  deleteTC,
  editTC,
  getTCByCode,
  getTownCouncils,
} from "../../../../../store/slices/town-council.slice";
import SearchBarComponent from "../../search-bar/search-bar.component";
import "../../../admin.scss";

interface TownCouncilTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: () => void;
  isLoading: boolean;
  onSearch: (val: string) => void;
}

const TownCouncilTableComponent: FC<TownCouncilTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
  onSearch,
}) => {
  const formRef = createRef();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDetails, setIsDetails] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [selectedCodeToDeleteOrEdit, setSelectedCodeToDeleteOrEdit] =
    useState<number>(0);
  const [initialValues, setInitialValues] = useState<any>({
    name: "",
    tcCode: "",
    address: "",
  });

  const dispatch = useDispatch();

  const tcDetails = useSelector((state: any) => {
    return state.townCouncil.tcDetails;
  });

  const isLoadingTcDetails = useSelector((state: any) => {
    return state.townCouncil.isLoadingTcDetails;
  });

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsDetails(false);
    setIsEdit(false);
    setIsDelete(false);

    setInitialValues({
      name: "",
      tcCode: "",
      address: "",
    });
  };

  const detailsAction = (tcCode: any) => {
    setIsDetails(true);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(false);

    dispatch(getTCByCode({ tcCode }))
      .then(() => {})
      .catch((error: any) => {
        console.log("Error in api call: ", error);
      });
  };

  const editAction = (tcCode: any) => {
    setIsEdit(true);
    setIsModalOpen(true);
    setIsDetails(false);
    setIsDelete(false);

    dispatch(getTCByCode({ tcCode }))
      .then(() => {})
      .catch((error: any) => {
        console.log("Error in api call: ", error);
      });

    setSelectedCodeToDeleteOrEdit(tcCode);
  };

  const deleteAction = (tcCode: number) => {
    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(true);

    setSelectedCodeToDeleteOrEdit(tcCode);
  };

  const handleDelete = () => {
    dispatch(deleteTC({ tcCode: selectedCodeToDeleteOrEdit })).then(() => {
      onRefetchData();
      handleCloseModal();
    });
  };

  useEffect(() => {
    if (!tcDetails || !Object.keys(tcDetails).length) return;

    setInitialValues({
      name: tcDetails.name,
      tcCode: tcDetails.tcCode,
      address: tcDetails.address,
    });
  }, [tcDetails]);

  const onSubmit = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();

      if (isObjectNullable(form.values)) {
        return;
      }

      if (
        !form?.values?.tcCode ||
        !form?.values?.name ||
        !form?.values?.address ||
        form?.values?.tcCode?.length < 2 ||
        form?.values?.tcCode?.length > 20 ||
        form?.values?.name?.length < 2 ||
        form?.values?.name?.length > 50 ||
        form?.values?.address?.length < 2 ||
        form?.values?.address?.length > 1000
      ) {
        return;
      }

      if (isEdit) {
        const formValues = form.values;
        const payload = { ...formValues, tcCode: selectedCodeToDeleteOrEdit };

        dispatch(editTC({ payload, tcCode: selectedCodeToDeleteOrEdit })).then(
          () => {
            handleCloseModal();
            onRefetchData();
          },
        );
      } else {
        dispatch(addTC({ payload: form.values })).then(() => {
          handleCloseModal();
          onRefetchData();
        });
      }
    }
  };

  const modalActionConfig: any = isDetails
    ? []
    : isDelete
      ? [
          {
            label: "Yes",
            event: handleDelete,
            variant: "primary",
          },
        ]
      : [
          {
            label: "Save",
            event: onSubmit,
            variant: "primary",
          },
        ];

  const actionConfig: any = [
    {
      label: "Edit",
      actionProperty: "towncouncilCode",
      event: editAction,
      variant: "secondary",
      className: "mx-2 my-1",
      moduleName: "Town Council",
      actionName: "Edit-Town Council",
      icon: faPencilAlt,
    },
    {
      label: "View",
      actionProperty: "towncouncilCode",
      event: detailsAction,
      variant: "info",
      className: "mx-2 my-1",
      moduleName: "Town Council",
      actionName: "View-Town Council",
      icon: faFileAlt,
    },
    {
      label: "Delete",
      actionProperty: "towncouncilCode",
      event: deleteAction,
      variant: "danger",
      className: "mx-2 my-1",
      moduleName: "Town Council",
      actionName: "Delete-Town Council",
      icon: faTrashAlt,
    },
  ];

  const emptyConfig = {
    title: "No Town Council Found",
    description: "",
  };

  const formConfig: FormConfig = {
    controls: [
      {
        name: "tcCode",
        label: "Town Council Code",
        placeholder: "PRPTC",
        type: "text",
        required: !isDetails,
        readOnly: isDetails || isEdit,
        disabled: isDetails || isEdit,
      },
      {
        name: "name",
        label: "Town Council Name",
        placeholder: "Jurong-Clementi",
        type: "text",
        required: !isDetails,
        readOnly: isDetails || isEdit,
        disabled: isDetails || isEdit,
      },
      {
        name: "address",
        label: "Town Council Address",
        placeholder: "Start typing address here...",
        type: "formatted-textarea",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
    ],
  };

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .min(2, "Town Council Name must be at least 2 characters")
      .max(50, "Town Council Name should not exceed 50 characters")
      .required("Town Council Name is required"),
    tcCode: yup
      .string()
      .min(2, "Town Council Code must be at least 2 characters")
      .max(20, "Town Council Code should not exceed 20 characters")
      .required("Town Council Code is required"),
    address: yup
      .string()
      .required("Town Council Address is required")
      .max(1000, "Address should not exceed 1000 characters"),
  });

  return (
    <div>
      <div>
        <div className="d-flex gap-3 justify-content-end table-generic-action table-toolbar">
          <SearchBarComponent
            placeholder="Search Town Council"
            methodToDispatch={getTownCouncils}
            getSearch={onSearch}
          />
          <HasPermission
            moduleName="Town Council"
            actionName="Add-Town Council"
            isRoute={false}
          >
            <B2Button
              className="icon-text-btn"
              variant="primary"
              onClick={() => {
                setInitialValues({
                  name: "",
                  tcCode: "",
                  address: "",
                });

                setIsModalOpen(true);
              }}
            >
              {/* <FontAwesomeIcon icon={faCirclePlus} className="fs-7 me-2" /> */}
              Add New Town Council
            </B2Button>
          </HasPermission>
        </div>
        <div className="my-3">
          <TableComponent
            tableHeaders={columns}
            emptyConfig={emptyConfig}
            data={data}
            totalNumberOfRecords={totalNumberOfRecords}
            pageSize={pageSize}
            current={current}
            onChangePage={onChangePage}
            actionConfig={actionConfig}
            onChangeSort={onChangeSort}
            isLoadingData={isLoading}
          />
        </div>
      </div>

      {/* modals */}

      {isModalOpen && (
        <ModalComponent
          isConformationModal={isDelete}
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          headerTitle={
            isDetails
              ? "Town Council Details"
              : isEdit
                ? "Edit Town Council"
                : isDelete
                  ? "Delete Town Council"
                  : "Add New Town Council"
          }
          actionConfig={modalActionConfig}
        >
          {isDelete ? (
            <h5 className="text-center">
              Are you sure to <b className="text-danger">Delete</b>?
            </h5>
          ) : (
            <SkeletonComponent
              wrapperHeight="280px"
              skeletonHeight="50px"
              isLoading={isLoadingTcDetails}
              count={3}
            >
              <FormBuilderComponent
                formRef={formRef}
                initialValues={initialValues}
                validation={validationSchema}
                formConfig={formConfig}
              />
            </SkeletonComponent>
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default TownCouncilTableComponent;
